import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, CATEGORY_LABEL } from "@/lib/api";
import { ImageSlider } from "@/components/site/ImageSlider";
import { ProductInfo } from "@/components/site/ProductInfo";

const ORDER = { Original: 0, "Canvas Print": 1, "Fine Art Poster": 2 };

const groupsOf = (l) => {
  const gs = (l.image_groups || []).filter((g) => g.images?.length);
  if (gs.length) return gs;
  return [{ group_name: CATEGORY_LABEL[l.category], images: (l.images || []).map((url, i) => ({ url, is_default: i === 0 })) }];
};
const urlsOf = (g) => [...(g?.images || [])].sort((a, b) => Number(!!b.is_default) - Number(!!a.is_default)).map((i) => i.url);

const MaterialTabs = ({ tabs, active, onPick }) => (
  <div className="mb-6 flex flex-wrap gap-3" role="tablist" data-testid="material-tabs">
    {tabs.map((t) => {
      const on = t.slug === active.slug && t.group === active.group;
      return (
        <button
          key={`${t.slug}-${t.group}`}
          role="tab"
          aria-selected={on}
          onClick={() => onPick(t)}
          data-testid={`material-tab-${t.slug}-${t.group}`}
          className={`pill ${on ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#FDFBF7]" : "border-[#D9D3C7] text-[#3A3733] hover:border-[#1A1A1A]"}`}
        >
          {t.name}{t.sold ? " — sold" : ""}
        </button>
      );
    })}
  </div>
);

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const known = useRef([]);
  const galleryRef = useRef(null);
  const [listings, setListings] = useState([]);
  const [tab, setTab] = useState(null);
  const [sizeImgs, setSizeImgs] = useState([]);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setSizeImgs([]);
    if (known.current.some((l) => l.slug === slug)) {
      setTab((t) => (t && t.slug === slug ? t : { slug, group: 0 }));
      return;
    }
    known.current = [];
    setListings([]);
    setTab(null);
    setMissing(false);
    api
      .get(`/products/${slug}`)
      .then(async ({ data: p }) => {
        const sib = await api.get("/products", { params: { artwork: p.artworkSlug } }).then((r) => r.data).catch(() => []);
        const all = [p, ...sib.filter((x) => x.id !== p.id)].sort((a, b) => ORDER[a.category] - ORDER[b.category]);
        known.current = all;
        setListings(all);
        setTab({ slug, group: 0 });
      })
      .catch(() => setMissing(true));
  }, [slug]);

  const p = listings.find((l) => l.slug === tab?.slug);
  const tabs = useMemo(() => listings.flatMap((l) => groupsOf(l).map((g, gi) => ({ slug: l.slug, group: gi, name: g.group_name, sold: l.isOriginal && l.isSold }))), [listings]);
  const images = useMemo(() => {
    if (!p) return [];
    if (sizeImgs.length) return sizeImgs;
    const gs = groupsOf(p);
    return urlsOf(gs[tab.group] || gs[0]);
  }, [p, tab, sizeImgs]);

  const pick = (t) => {
    setSizeImgs([]);
    setTab(t);
    if (t.slug !== slug) navigate(`/artwork/${t.slug}`, { replace: true });
  };

  const pickSize = (v) => {
    const urls = (v?.images || []).map((i) => i.url).filter(Boolean);
    setSizeImgs(urls);
    if (urls.length && window.innerWidth < 1024) galleryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (missing && !p) {
    return (
      <div className="container-x py-32" data-testid="product-not-found">
        <h1 className="font-display text-5xl font-light">This work could not be found.</h1>
        <Link to="/gallery" className="link-u mt-10">Return to the collection</Link>
      </div>
    );
  }

  return (
    <div className="container-x py-12 sm:py-16" data-testid="product-detail-page">
      <Link to="/gallery" className="eyebrow transition-colors hover:text-[#1A1A1A]" data-testid="product-back-link">← The collection</Link>
      {!p ? (
        <div className="mt-10 aspect-[4/5] w-full max-w-2xl animate-pulse bg-[#F2EEE6]" />
      ) : (
        <div className="mt-10 grid gap-14 lg:grid-cols-12 lg:gap-20">
          <div className="scroll-mt-24 lg:col-span-7" ref={galleryRef} data-testid="product-gallery">
            {tabs.length > 1 && <MaterialTabs tabs={tabs} active={tab} onPick={pick} />}
            <ImageSlider images={images} title={p.title} isOriginal={p.isOriginal && !sizeImgs.length} />
          </div>
          <div className="lg:col-span-5">
            <ProductInfo key={p.id} p={p} original={listings.find((l) => l.isOriginal)} onVariant={pickSize} />
          </div>
        </div>
      )}
    </div>
  );
}
